import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { ShieldCheck, Copy, Check, ArrowRight } from 'lucide-react';
import { useTheme } from '../ui/ThemeContext';
import { useLanguage } from '../i18n/LanguageContext';
import { inputClass, primaryButtonClass, linkClass, labelClass, Spinner, mfaErrorText } from './MfaStep';

/**
 * Erzwungene MFA-Einrichtung beim ersten Login (SAAS-MODUS-DESIGN §5.2):
 * QR-Code + Geheimnis zum Abtippen -> erster Code -> Wiederherstellungscodes genau einmal anzeigen.
 * Die Sitzung startet erst, wenn der Benutzer die Aufbewahrung bestätigt (finish).
 */
const MfaEnroll = ({ mfaToken, setupMfa, completeMfa, setError, onBack }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [setup, setSetup] = useState(null); // { qr, secret }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { recoveryCodes, finish }
  const [copied, setCopied] = useState(false);
  const [stored, setStored] = useState(false);
  const started = useRef(false); // StrictMode: Einrichtung nur einmal anstoßen (jede erzeugt ein neues Geheimnis)
  const muted = isDarkMode ? 'text-white/60' : 'text-slate-500';

  const fail = (err) => {
    if (err.expired) return onBack(t('login.mfa.expired'));
    setError(mfaErrorText(err, t));
  };

  // Solange die Codes sichtbar und nicht bestätigt sind: Warnung beim Verlassen/Neuladen (Codes kämen nie wieder).
  useEffect(() => {
    if (!result) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [result]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const { otpauthUri, secret } = await setupMfa(mfaToken);
        setSetup({ qr: await QRCode.toDataURL(otpauthUri, { width: 200, margin: 1 }), secret });
      } catch (err) { fail(err); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return setError(t('login.mfa.enterCode'));
    setBusy(true);
    setError('');
    try {
      const res = await completeMfa({ mfaToken, code });
      if (res.recoveryCodes) setResult(res);
    } catch (err) {
      fail(err);
      setCode('');
    }
    setBusy(false);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(result.recoveryCodes.join('\n'));
      setCopied(true);
    } catch {
      setError(t('login.mfa.copyFailed'));
    }
  };

  if (result) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('login.mfa.codesTitle')}</h2>
          <p className={`text-sm ${muted}`}>{t('login.mfa.codesHint')}</p>
        </div>
        <div className={`grid grid-cols-2 gap-2 p-4 rounded-2xl font-mono text-center tracking-wider ${isDarkMode ? 'bg-gray-800/30 border border-white/30 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
          {result.recoveryCodes.map((c) => <span key={c}>{c}</span>)}
        </div>
        <button type="button" onClick={copyCodes} className={`${linkClass(isDarkMode)} flex items-center gap-2`}>
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {t(copied ? 'login.mfa.codesCopied' : 'login.mfa.copyCodes')}
        </button>
        <label className={`flex items-start gap-3 text-sm cursor-pointer ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
          <input type="checkbox" checked={stored} onChange={(e) => setStored(e.target.checked)} className="mt-0.5 w-4 h-4" />
          {t('login.mfa.codesStored')}
        </label>
        <button type="button" disabled={!stored} onClick={result.finish} className={primaryButtonClass}>
          <span>{t('login.mfa.continue')}</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <h2 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('login.mfa.setupTitle')}</h2>
        <p className={`text-sm ${muted}`}>{t('login.mfa.setupHint')}</p>
      </div>

      {setup ? (
        <div className="text-center space-y-3">
          <img src={setup.qr} alt={t('login.mfa.qrAlt')} className="mx-auto w-48 h-48 rounded-xl bg-white p-2" />
          <p className={`text-xs ${muted}`}>{t('login.mfa.manualHint')}</p>
          <code className={`block break-all text-sm font-mono select-all ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
          </code>
        </div>
      ) : (
        <div className={`flex justify-center py-8 ${muted}`}>
          <span className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        </div>
      )}

      <div>
        <label htmlFor="mfa-code" className={labelClass(isDarkMode)}>{t('login.mfa.codeLabel')}</label>
        <input
          id="mfa-code"
          disabled={busy || !setup}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          placeholder="123456"
          className={`${inputClass(isDarkMode)} tracking-widest text-center text-lg`}
        />
      </div>

      <button type="submit" disabled={busy || !setup} className={primaryButtonClass}>
        {busy ? <Spinner /> : <ShieldCheck className="w-5 h-5" />}
        <span>{t('login.mfa.finishSetup')}</span>
      </button>

      <div className="flex justify-end">
        <button type="button" onClick={() => onBack('')} disabled={busy} className={linkClass(isDarkMode)}>{t('login.mfa.back')}</button>
      </div>
    </form>
  );
};

export default MfaEnroll;
